import { getClosestElement, getElements } from '../Utility/Dom'
import { SWAP_EVENT } from '../Utility/SwapReinit'
import { push } from './DataLayer'
import { type DataLayerPayload } from './Types'

/**
 * Lets any template emit a data layer push without TypeScript. Keep payloads free of personal data.
 *
 *   <div data-js="tracking-event" data-payload="{f:format.json(value: {...}, forceObject: true)}" hidden></div>
 *   <a href="…" data-js="tracking-click" data-payload="{f:format.json(value: {...}, forceObject: true)}">…</a>
 */
const CLICK_IDENTIFIER = 'tracking-click'
const EVENT_IDENTIFIER = 'tracking-event'
const PUSHED_FLAG = 'trackingPushed'

const readPayload = (element: HTMLElement): DataLayerPayload | null => {
  const raw = element.dataset.payload
  if (raw === undefined || raw === '') {
    return null
  }

  try {
    const parsed: unknown = JSON.parse(raw)

    return typeof parsed === 'object' && parsed !== null && Array.isArray(parsed) === false
      ? (parsed as DataLayerPayload)
      : null
  } catch {
    // A malformed payload is a template error and must never break the page.
    return null
  }
}

const pushDeclaredEvents = (): void => {
  for (const element of getElements(EVENT_IDENTIFIER)) {
    // A re-rendered course-search fragment must report only its own fresh payload.
    if (element.dataset[PUSHED_FLAG] === 'true') {
      continue
    }

    element.dataset[PUSHED_FLAG] = 'true'

    const payload = readPayload(element)
    if (payload !== null) {
      push(payload)
    }
  }
}

export const observeDeclaredEvents = (): void => {
  pushDeclaredEvents()

  document.addEventListener(SWAP_EVENT, pushDeclaredEvents)

  // Delegated, so clickable payloads survive an AJAX swap.
  document.addEventListener('click', (event: Event): void => {
    const element = getClosestElement(event.target as HTMLElement, CLICK_IDENTIFIER)
    if (element === null) {
      return
    }

    const payload = readPayload(element)
    if (payload !== null) {
      push(payload)
    }
  })
}
